import { useEffect, useState } from 'react';
import type {
  Certificate,
  CertificateEvidence,
  DeclaredCertificate,
  VerificationType,
} from '@mvcp/contracts';
import { getEvidenceDownloadUrl, listCertificateEvidence } from '../api';
import { StatusPill } from './statusPill';

/** The date portion of an ISO timestamp (`2027-01-31`), or an em-dash when absent. */
function expiryDate(iso?: string): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  return new Date(t).toISOString().slice(0, 10);
}

/**
 * Provenance ("assurance") marker labels (Story 5.2), orthogonal to the status pill.
 * Re-inlined here because the web consumes `@mvcp/contracts` as TYPES ONLY (a
 * runtime-value import breaks `vite build`, Story 1.6). Honest wording: `self_declared`
 * is NEVER presented as equivalent to a third-party certification.
 */
const PROVENANCE_LABEL: Record<VerificationType, string> = {
  third_party: 'Third-party verified',
  self_declared: 'Self-declared',
};

/**
 * The provenance badge shown alongside — and independent of — the status pill. Always
 * present when the framework's `verification_type` resolves; screen-reader announced
 * via an explicit accessible name so lower assurance is never mistaken for higher.
 */
function ProvenanceBadge({ verificationType }: { verificationType: VerificationType }): React.JSX.Element {
  const label = PROVENANCE_LABEL[verificationType];
  return (
    <span
      className="status-pill"
      data-tone="neutral"
      data-provenance={verificationType}
      aria-label={`Provenance: ${label}`}
    >
      {label}
    </span>
  );
}

/**
 * The opt-in manager evidence cell (Story 6.10, FR-11, AR-14) — rendered ONLY when the
 * host passes `manageEvidence` (the Compliance-Manager cockpit; the vendor portal and
 * admin console pass it not, so the leaf stays read-only). It lists a certificate's
 * evidence and offers Download (a short-lived signed URL) for the most-recent CLEAN row.
 *
 * Story 6.10 RETIRED the manager evidence-ATTACH path: evidence is now attached at
 * submit-for-verification time via the shared declare pipeline, and the backbone
 * `certificate_evidence` INSERT is System-Administrator-only (the admin promote). So this
 * cell is Download-only — it serves evidence on already-verified backbone certificates.
 * Evidence is NEVER presented as trusted; the status pill remains the row's source of truth.
 */
function EvidenceActions({
  certificate,
  label,
}: {
  certificate: Certificate;
  /** Human framework label used in per-row accessible names. */
  label: string;
}): React.JSX.Element {
  const [evidence, setEvidence] = useState<CertificateEvidence[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await listCertificateEvidence(certificate.id);
        if (!cancelled) setEvidence(rows);
      } catch {
        // A list failure is non-fatal — Download simply is not offered (never a
        // misleading "download" for evidence we couldn't read).
        if (!cancelled) setEvidence([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [certificate.id]);

  async function onDownload(ev: CertificateEvidence): Promise<void> {
    setError(null);
    // Open the tab SYNCHRONOUSLY inside the click gesture, then navigate it once the
    // signed URL resolves — opening AFTER the await loses the user-activation and the
    // browser blocks it as a programmatic popup (mirrors VerificationQueueView).
    const tab = window.open('', '_blank');
    try {
      const { url } = await getEvidenceDownloadUrl(certificate.id, ev.id);
      if (tab) tab.location.href = url;
      else setError('Allow pop-ups for this site to download evidence.');
    } catch (err: unknown) {
      tab?.close();
      setError(err instanceof Error ? err.message : "Couldn't download the evidence.");
    }
  }

  // Only `clean` evidence is downloadable (server-side: `infected` never; a
  // cross-tenant caller requires `clean`) — offer Download for the most-recent clean
  // row so a non-trusted/non-clean row is never presented as retrievable (AC4). The
  // list is newest-first (server `ORDER BY created_at DESC`), so the FIRST clean row
  // is the most recent — do NOT reverse (that would surface the oldest/superseded file).
  const latest = evidence ? evidence.find((e) => e.scanState === 'clean') ?? null : null;
  if (!latest) {
    return <span className="muted" style={{ fontSize: 13 }}>—</span>;
  }
  return (
    <div className="stack" style={{ gap: 4 }}>
      <button
        type="button"
        className="icon-button"
        onClick={() => void onDownload(latest)}
        aria-label={`Download evidence (${label})`}
      >
        Download
      </button>
      {error && (
        <span role="alert" aria-live="assertive" className="status-pill" data-tone="bad">
          <span className="status-pill__dot" aria-hidden="true" />
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * The certificate leaf (Story 4.4) shared by the Vendor and Device cockpit details.
 * Renders each certificate as a row: framework code (mono) + a status pill (dot + text
 * label) + an OPTIONAL provenance badge (Story 5.2, orthogonal to status) + expiry
 * date (mono). On the Vendor portal it also renders the Vendor's own DECLARED
 * (proposed) certificates as "Declared · pending review" rows (warn tone), which
 * persist until Story 5.4 verification resolves them.
 *
 * `frameworkCode` maps a `frameworkId` → its short code; the raw id is the fallback.
 * `frameworkVerificationType` (optional) maps a `frameworkId` → its `verification_type`
 * for the provenance badge. Wired by every surface that renders a certificate — the
 * vendor portal and both company-facing cockpit details (Vendor and Device) — because
 * DESIGN.md requires a self-declared item to ALWAYS carry the marker: it can read
 * "Certified" on status alone, and unmarked that is indistinguishable from a
 * third-party certificate (PRD §5.5 honesty rule). The prop stays optional so the
 * badge simply does not render where no map is supplied.
 *
 * Story 6.10: `manageEvidence` OPTS IN the Compliance-Manager Download control (an extra
 * "Evidence" column). Absent (vendor portal / admin console), the leaf stays strictly
 * read-only. The manager evidence-ATTACH path was retired in 6.10 (evidence attaches at
 * submit-for-verification time); this column now serves Download only. Declared/pending
 * rows never carry evidence controls (they are pre-verification staging rows).
 */
export function CertificateRows({
  certificates,
  frameworkCode,
  declared = [],
  frameworkVerificationType,
  manageEvidence = false,
}: {
  certificates: Certificate[];
  frameworkCode: (frameworkId: string) => string;
  declared?: DeclaredCertificate[];
  frameworkVerificationType?: (frameworkId: string) => VerificationType | undefined;
  /** Opt in the manager Download control (Story 6.10). Default: read-only. */
  manageEvidence?: boolean;
}): React.JSX.Element {
  if (certificates.length === 0 && declared.length === 0) {
    return (
      <p className="muted" style={{ margin: 0 }}>
        No certificates yet.
      </p>
    );
  }
  const provenance = (frameworkId: string): VerificationType | undefined =>
    frameworkVerificationType?.(frameworkId);
  return (
    <div className="table-scroll">
      <table className="data-table">
        <thead>
          <tr>
            <th>Framework</th>
            <th>Status</th>
            <th>Expires</th>
            {manageEvidence && <th>Evidence</th>}
          </tr>
        </thead>
        <tbody>
          {certificates.map((c) => {
            const vt = provenance(c.frameworkId);
            return (
              <tr key={c.id}>
                <td data-numeric>{frameworkCode(c.frameworkId)}</td>
                <td>
                  <span className="cell-cluster">
                    <StatusPill status={c.status} />
                    {vt && <ProvenanceBadge verificationType={vt} />}
                  </span>
                </td>
                <td data-numeric>{expiryDate(c.expiresAt)}</td>
                {manageEvidence && (
                  <td>
                    <EvidenceActions certificate={c} label={frameworkCode(c.frameworkId)} />
                  </td>
                )}
              </tr>
            );
          })}
          {declared.map((d) => {
            const vt = provenance(d.frameworkId);
            return (
              <tr key={`declared-${d.id}`} data-declared="true">
                <td data-numeric>{frameworkCode(d.frameworkId)}</td>
                <td>
                  <span className="cell-cluster">
                    <StatusPill status="pending" />
                    <span className="muted">Declared · pending review</span>
                    {vt && <ProvenanceBadge verificationType={vt} />}
                  </span>
                </td>
                <td data-numeric>{expiryDate(d.expiresAt)}</td>
                {manageEvidence && <td />}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
